import { useEffect, useState } from "react"

import { Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Icon from "../../../../Icon"
import { ACTION_NEEDED_REASONS } from "../../../../Utils/creditAutomationReasons"

import styles from "./Banner.module.less"

const { Text, Link } = Typography

// CHA1-3324: per-status/reason Intercom articles linked from the banner's "Learn more."
const STATUS_CONFIG = {
  active: {
    variant: "active",
    icon: "circle_check_green",
    text: "Credit automation is active. New requests will be processed automatically.",
    learnMoreUrl:
      "https://app.intercom.com/a/apps/i3j5um3v/knowledge-hub/all-content?activeContentId=20534334&activeContentType=article&editorMode=view&native_content=false"
  },
  pending: {
    variant: "pending",
    icon: "arrows_circle",
    text: "Credit automation is pending while we verify your payment processor access. This usually takes one business day.",
    learnMoreUrl:
      "https://app.intercom.com/a/apps/i3j5um3v/knowledge-hub/all-content?activeContentId=20534225&activeContentType=article&editorMode=view&native_content=false"
  },
  action_needed: {
    variant: "actionNeeded",
    icon: "warning_full"
    // text/learnMoreUrl come from the matching ACTION_NEEDED_REASONS entry, see below
  },
  lost: {
    variant: "lost",
    icon: "warning_full",
    text: "Credits are no longer automated due to access to _name_ missing. Please set it up again to continue using automated alerts.",
    learnMoreUrl:
      "https://app.intercom.com/a/apps/i3j5um3v/knowledge-hub/all-content?activeContentId=20534388&activeContentType=article&editorMode=view&native_content=false"
  }
}

const SHOPIFY_EXPIRED_REASONS = ["The access request has expired", "Shopify request has expired"]
const SHOPIFY_REQUEST_WILL_BE_SENT_ARTICLE =
  "https://app.intercom.com/a/apps/i3j5um3v/knowledge-hub/all-content?activeContentId=20633627&activeContentType=article&editorMode=view&native_content=false"
const SHOPIFY_REQUEST_EXPIRED_ARTICLE =
  "https://app.intercom.com/a/apps/i3j5um3v/knowledge-hub/all-content?activeContentId=20633629&activeContentType=article&editorMode=view&native_content=false"

// Live "minutes left" until `expiresAt`, re-computed every 30s. Returns null when there's no deadline.
const useMinutesLeft = (expiresAt) => {
  const [now, setNow] = useState(() => new Date().getTime())
  useEffect(() => {
    if (!expiresAt) return undefined
    const id = setInterval(() => setNow(new Date().getTime()), 30_000)
    return () => clearInterval(id)
  }, [expiresAt])
  if (!expiresAt) return null
  const ms = new Date(expiresAt).getTime() - now
  return Math.max(0, Math.ceil(ms / 60_000))
}

const isShopify = (processor) => processor?.service_name === "shopify"

// `status` is this repo's internal key (mapped from the API's Title Case aggregate `status`).
// `processor` is the first Pending/Action needed/Lost processor's full row (CHA1-2308), used for the
// name, reason, and — for Shopify — its scraper fields (`accept_url`, `access_request_expires_at`,
// `is_access_request_expired`). `onResubmit` reopens the Automate credits flow from the banner.
export const Banner = ({ status, processor, onResubmit }) => {
  const { t } = useTranslation()
  const minutesLeft = useMinutesLeft(processor?.access_request_expires_at)

  const processorName = processor?.display_name
  const reason = processor?.failure_reason

  if (isShopify(processor)) {
    if (processor.is_access_request_expired || SHOPIFY_EXPIRED_REASONS.includes(reason)) {
      return (
        <div className={cls(styles.banner, styles.actionNeeded)}>
          <Icon type="warning_full" className={styles.icon} />
          <Text className={styles.text}>
            {t(
              "We couldn't verify access to Shopify: Your Shopify access request has expired. Submit a new request to continue setting up Credit Automation."
            )}{" "}
            {onResubmit && (
              <Link className={styles.link} onClick={onResubmit}>
                {t("Submit a new request.")}
              </Link>
            )}{" "}
            <Link
              className={styles.link}
              href={SHOPIFY_REQUEST_EXPIRED_ARTICLE}
              target="_blank"
              rel="noreferrer"
            >
              {t("Learn more.")}
            </Link>
          </Text>
        </div>
      )
    }

    if (status === "pending") {
      // Scraper failed: an admin will send the collaborator request manually later.
      if (processor.is_manual_access_request) {
        return (
          <div className={cls(styles.banner, styles.pending)}>
            <Icon type="arrows_circle" className={styles.icon} />
            <Text className={styles.text}>
              {t(
                "The request to your Shopify access will be sent within 24 hours. Once received, accept it within 48 hours to activate Credit Automation."
              )}{" "}
              <Link
                className={styles.link}
                href={SHOPIFY_REQUEST_WILL_BE_SENT_ARTICLE}
                target="_blank"
                rel="noreferrer"
              >
                {t("Learn more.")}
              </Link>
            </Text>
          </div>
        )
      }
      // Scraper active: the request is already in the merchant's store to accept within the window.
      if (processor.accept_url) {
        return (
          <div className={cls(styles.banner, styles.pending)}>
            <Icon type="arrows_circle" className={styles.icon} />
            <Text className={styles.text}>
              {minutesLeft === null
                ? t(
                    "Your Shopify access request has been sent. Accept it to activate Credit Automation."
                  )
                : t(
                    "Your Shopify access request has been sent. Accept it within the next _min_ min to activate Credit Automation.",
                    { min: minutesLeft }
                  )}{" "}
              <Link
                className={styles.link}
                href={processor.accept_url}
                target="_blank"
                rel="noreferrer"
              >
                {t("Accept request.")}
              </Link>
            </Text>
          </div>
        )
      }
      // Neither manual nor an open scraper request (e.g. scraper off on dev-5):
      // fall through to the generic Pending banner below.
    }
  }

  // ---- Generic banners (all other processors / statuses) ----
  const config = STATUS_CONFIG[status]
  if (!config) return null

  let text
  let learnMoreUrl = config.learnMoreUrl
  if (status === "action_needed") {
    const reasonEntry = ACTION_NEEDED_REASONS.find((entry) => entry.value === reason)
    if (reasonEntry) {
      text = t(reasonEntry.bannerText, { name: processorName })
      learnMoreUrl = reasonEntry.learnMoreUrl
    } else {
      text = reason ? t(reason) : t("Action needed on your payment account.")
      learnMoreUrl = undefined
    }
  } else if (config.text.includes("_name_")) {
    text = t(config.text, { name: processorName })
  } else {
    text = t(config.text)
  }

  return (
    <div className={cls(styles.banner, styles[config.variant])}>
      <Icon type={config.icon} className={styles.icon} />
      <Text className={styles.text}>
        {text}
        {learnMoreUrl && (
          <>
            {" "}
            <Link className={styles.link} href={learnMoreUrl} target="_blank" rel="noreferrer">
              {t("Learn more.")}
            </Link>
          </>
        )}
      </Text>
    </div>
  )
}

export default Banner
